import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { getOfficeEducation } from '~/actions';
import { useAppSelector } from '~/hooks/useAppSelector';
import type { IOfficeEducationResponse } from '~/models/EducationResponse';
import { EducationUtils } from '~/utils/EducationUtils';
import { filterObjectKeys } from '~/utils/Object';
import { SoftwareUtils } from '~/utils/SoftwareUtils';

type TOfficeEducationCatalog = NonNullable<IOfficeEducationResponse['DNT']>;

/**
 * The specialty, software and insurance choices an office picks from (the DNT
 * catalog, the same for every location). Loads it when nothing has yet.
 */
export const useOfficeEducationOptions = () => {
  const dispatch = useDispatch();
  // The reducer stores the response's `DNT` branch, though the state type names the whole response.
  const catalog = useAppSelector(
    (state) => state.user.officeEducation as TOfficeEducationCatalog | null,
  );

  useEffect(() => {
    if (!catalog) dispatch(getOfficeEducation());
  }, [dispatch, catalog]);

  return useMemo(
    () => ({
      specialties: Object.keys(
        filterObjectKeys(catalog?.specialtiesAndProcedures, 'specialty'),
      ).map((code) => ({
        value: code,
        label: EducationUtils.removeSpecialtyPrefix(code) as string,
      })),
      /** Display names, as the office sees and picks them. */
      softwares: SoftwareUtils.getSoftwareTranslations(catalog?.softwares) as string[],
      insurances: catalog?.insurances ?? [],
    }),
    [catalog],
  );
};
